import React from 'react';
import { compose } from 'redux';
import {
  PapiGallection,
  PapiDeviation,
  PapiPremiumFolderData,
} from '@wix/da-types-papi';
import OverlayContext from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard/Overlay/OverlayContext';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { scrollIntoViewWithHeader } from '@wix/da-shared-react/pkg/utils/scrollIntoViewWithHeader';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import { GallectionType } from '../../../../../../types/gallection';
import { getGallectionsStickyBarHeight } from '../../helpers';
import { getStickyContentHeight } from '../../../helpers';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { Url } from '@wix/da-url';
import {
  BiEventType,
  BiLoggerContextProvider,
} from '@wix/da-shared-react/pkg/biLogger';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import withFeatureFlags, {
  WithFeatureFlagsProps,
} from '@wix/da-shared-react/pkg/redux/flags/withFeatureFlags';
import GallectionDeviationsTorpedo from '../../GallectionDeviationsTorpedo';
import FolderPaywall from '../../FolderPaywall';
import FolderDescription from '../FolderDescription';
import FolderBreadcrumbs from '../FolderBreadcrumbs/';

import s from './FolderInfiniteContent.scss';

const FOLDER_BAR_DISTANCE_FROM_TOP = 400;

export interface Props {
  streamId: string;
  folderId: number;
  subfolderId?: number;
  folder: PapiGallection;
  subfolder?: PapiGallection;
  gallectionType: GallectionType;
  deviations: PapiDeviation[];
  isInitialized: boolean;
  hasMore: boolean;
  isSelected: boolean;
  isFetching: boolean;
  initFetch: () => void;
  fetchMore: () => void;
  isOwner: boolean;
  className?: string;
  isMobile?: boolean;
  scrollIntoViewWhenLoaded?: boolean;
  justExitedDuperbrowse?: boolean;
  isViewingAsVisitor: boolean;
}

class FolderInfiniteContent extends React.Component<
  Props & WithFeatureFlagsProps
> {
  static displayName = 'FolderInfiniteContent';

  folderRef: React.RefObject<HTMLDivElement> = React.createRef();
  torpedoRef: React.RefObject<HTMLDivElement> = React.createRef();

  UNSAFE_componentWillMount() {
    this.initFetchIfNeeded(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.initFetchIfNeeded(nextProps);
  }

  initFetchIfNeeded = props => {
    const { deviations, isInitialized, isFetching, initFetch } = props;

    if (!deviations.length && !isInitialized && !isFetching) {
      initFetch();
    }
  };

  render() {
    const {
      subfolder,
      deviations,
      isFetching,
      folder,
      className,
      gallectionType,
      hasMore,
      streamId,
      isOwner,
      isViewingAsVisitor,
    } = this.props;

    const { pointPrice, isPremiumLocked } = normalizePremiumFolderData(
      folder.premiumFolderData
    );
    const isCollection = gallectionType === GallectionType.COLLECTION;
    return (
      <OverlayContext.Provider
        value={{
          showAuthorTooltip: isCollection,
          showPermanentIconWhenFaved: !(isCollection && isOwner),
        }}
      >
        <div className={className} ref={this.folderRef}>
          <ErrorBoundary>
            <FolderDescription
              folder={folder}
              className={s['folder-description']}
            />
            {subfolder && <FolderBreadcrumbs />}
            {isPremiumLocked && (!isOwner || isViewingAsVisitor) ? (
              <FolderPaywall
                premiumFolderData={{
                  ...(folder.premiumFolderData as PapiPremiumFolderData),
                  galleryUrl: Url.userGalleryLink({
                    username: folder.owner.username,
                    gallectionType: 'gallery',
                    folderId: folder.folderId,
                    folderName: folder.name,
                  }),
                }}
                folder={folder}
                biData={{
                  evid: BiEventType.GALLERY_PURCHASE,
                  points: pointPrice,
                  typeid: gallectionTypeToResourceType(gallectionType),
                  itemid: folder.folderId,
                  galleryid: folder.folderId,
                  authorid: folder.owner.useridUuid,
                }}
              />
            ) : (
              deviations.length > 0 && (
                <OnScrollBottom
                  distanceToBottom={2}
                  distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
                  onScrolledToBottom={hasMore ? this.onScrollBottom : undefined}
                >
                  <div>
                    <div ref={this.torpedoRef}>
                      <BiLoggerContextProvider
                        value={{
                          section_typeid: gallectionTypeToResourceType(
                            gallectionType
                          ),
                          sectionid: folder.folderId,
                          authorid: folder.owner.useridUuid,
                        }}
                      >
                        <GallectionDeviationsTorpedo
                          streamId={streamId}
                          gallectionType={gallectionType}
                          deviations={deviations}
                        />
                      </BiLoggerContextProvider>
                    </div>
                  </div>
                </OnScrollBottom>
              )
            )}
            {isFetching && <LoadingIndicator />}
          </ErrorBoundary>
        </div>
      </OverlayContext.Provider>
    );
  }

  onScrollBottom = scrollDelta => {
    const { hasMore, isFetching, fetchMore } = this.props;
    if (isFetching) {
      return;
    }
    if (!hasMore) {
      return;
    }
    fetchMore();
  };

  isTorpedoVisible = () => {
    const { isMobile } = this.props;
    const domNode = this.torpedoRef.current;
    if (!domNode) {
      return false;
    }
    const offsetTop =
      getStickyContentHeight(isMobile) +
      getGallectionsStickyBarHeight(isMobile);
    const { bottom } = domNode.getBoundingClientRect();
    return bottom - offsetTop > 0;
  };

  scrollToThisFolder(isInitialLoad = true) {
    const thisFolder = this.folderRef.current;
    const { isMobile } = this.props;
    if (!thisFolder) {
      return;
    }

    const folderBarHeightAndPadding =
      getGallectionsStickyBarHeight(isMobile) + 15;
    scrollIntoViewWithHeader(
      thisFolder,
      isMobile,
      // on the initial page load for some reason, we need to move
      // 400px further down in order to get the top of the folder
      // To add this with scrollIntoViewWithHeader, we need to subtract it
      // from the header height that will be subtracted from the folder's
      // bounds.top
      isInitialLoad
        ? folderBarHeightAndPadding - FOLDER_BAR_DISTANCE_FROM_TOP
        : folderBarHeightAndPadding
    );
  }
}

const enhance = compose(withMobileContext, withFeatureFlags);

export default enhance(FolderInfiniteContent);
